import {
  getInbox,
  getInboxUpdates,
  getInboxDeal,
  getInboxMessages,
  sendInboxMessage,
  inboxDealAction,
  starInboxDeal,
  readInboxDeal,
  unreadInboxDeal,
  hideInboxDeal,
  ackInboxDeal,
  getTransactionFiles,
} from '../../util/api';
import { parse } from '../../util/urlHelpers';
import { newClientId } from '../../util/dealMessages';
import { setUnreadMessagesLog, setMemberAreaState } from '../../ducks/user.duck';

/**
 * The new inbox page (docs/new-inbox-and-deal-flow.md): the list on the left,
 * the open deal in the middle, its panel on the right. Everything comes from
 * /api/inbox (server/api/inbox), never from Sharetribe.
 */

// ================ Action types ================ //

const LIST_REQUEST = 'app/DealInboxPage/LIST_REQUEST';
const LIST_SUCCESS = 'app/DealInboxPage/LIST_SUCCESS';
const LIST_ERROR = 'app/DealInboxPage/LIST_ERROR';

const DEAL_REQUEST = 'app/DealInboxPage/DEAL_REQUEST';
const DEAL_SUCCESS = 'app/DealInboxPage/DEAL_SUCCESS';
const DEAL_ERROR = 'app/DealInboxPage/DEAL_ERROR';
const DEAL_CLEAR = 'app/DealInboxPage/DEAL_CLEAR';

const MESSAGES_REQUEST = 'app/DealInboxPage/MESSAGES_REQUEST';
const MESSAGES_SUCCESS = 'app/DealInboxPage/MESSAGES_SUCCESS';
const MESSAGES_ERROR = 'app/DealInboxPage/MESSAGES_ERROR';

const SEND_REQUEST = 'app/DealInboxPage/SEND_REQUEST';
const SEND_SUCCESS = 'app/DealInboxPage/SEND_SUCCESS';
const SEND_ERROR = 'app/DealInboxPage/SEND_ERROR';

const ACTION_REQUEST = 'app/DealInboxPage/ACTION_REQUEST';
const ACTION_DONE = 'app/DealInboxPage/ACTION_DONE';
const ACTION_ERROR = 'app/DealInboxPage/ACTION_ERROR';

const FILES_REQUEST = 'app/DealInboxPage/FILES_REQUEST';
const FILES_SUCCESS = 'app/DealInboxPage/FILES_SUCCESS';
const FILES_ERROR = 'app/DealInboxPage/FILES_ERROR';

const UPDATES_SUCCESS = 'app/DealInboxPage/UPDATES_SUCCESS';
const DISMISS_UPDATE_NOTE = 'app/DealInboxPage/DISMISS_UPDATE_NOTE';

// ================ Reducer ================ //

export const TABS = ['listings', 'enquiries'];

const initialState = {
  // tab null: none in the link; the page picks one (PAR-747, inboxTabFor).
  query: { tab: null, stage: 'all', chip: null },
  rows: [],
  counts: null,
  listMeta: null,
  listLoadedAt: 0,
  listInProgress: false,
  listError: null,

  dealId: null,
  deal: null,
  dealLoadedAt: 0,
  dealInProgress: false,
  dealError: null,

  // Oldest first, as they are shown.
  messages: [],
  messagesMeta: null,
  messagesInProgress: false,
  messagesError: null,

  sendInProgress: false,
  sendError: null,

  // The action being run ('accept-application', 'star', ...), and why the
  // last one was refused.
  actionInProgress: null,
  actionError: null,

  files: [],
  filesInProgress: false,
  filesError: null,

  // The open deal was changed by someone else since it was shown (the
  // inbox refresh found it): the chat says so until it is dismissed.
  dealUpdatedNote: false,
};

const errorOf = e => ({ error: e?.error || 'failed', missing: e?.missing || null, reason: e?.reason || null });

/**
 * Whether the refreshed deal differs in a way the member should be told
 * about: its state, or its introduction request. Changes the member made
 * themselves are already on screen (runOnDeal), so a difference here was made
 * by someone else. Pure.
 */
export const dealChangedBetween = (before, after) =>
  !!before &&
  !!after &&
  (before.state !== after.state ||
    (before.introduction?.status || null) !== (after.introduction?.status || null));

const sameQuery = (a, b) =>
  a.tab === b.tab && a.stage === b.stage && (a.chip || null) === (b.chip || null);

// Messages arrive newest first; pages are merged by id and kept oldest first.
const mergeMessages = (current, incoming) => {
  const byId = new Map(current.map(m => [m.id, m]));
  incoming.forEach(m => byId.set(m.id, m));
  return [...byId.values()].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt) || (a.id > b.id ? 1 : -1)
  );
};

export default function dealInboxPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case LIST_REQUEST:
      return { ...state, query: payload, listInProgress: true, listError: null };
    case LIST_SUCCESS:
      return {
        ...state,
        rows: payload.rows,
        counts: payload.counts,
        listMeta: payload.meta,
        listLoadedAt: Date.now(),
        listInProgress: false,
      };
    case LIST_ERROR:
      return { ...state, listInProgress: false, listError: payload };

    case DEAL_REQUEST: {
      const sameDeal = state.dealId === payload;
      return {
        ...state,
        dealId: payload,
        deal: sameDeal ? state.deal : null,
        messages: sameDeal ? state.messages : [],
        messagesMeta: sameDeal ? state.messagesMeta : null,
        files: sameDeal ? state.files : [],
        dealInProgress: true,
        dealError: null,
        actionError: null,
        sendError: null,
        dealUpdatedNote: sameDeal ? state.dealUpdatedNote : false,
      };
    }
    case DEAL_SUCCESS:
      if (payload.id !== state.dealId) return state;
      return {
        ...state,
        deal: payload,
        dealLoadedAt: Date.now(),
        dealInProgress: false,
        // The row shows what the deal now shows.
        rows: state.rows.map(r =>
          r.id === payload.id
            ? { ...r, starred: payload.starred, unreadCount: payload.unreadCount }
            : r
        ),
      };
    case DEAL_ERROR:
      return { ...state, dealInProgress: false, dealError: payload };
    case DEAL_CLEAR:
      return {
        ...state,
        dealId: null,
        deal: null,
        messages: [],
        messagesMeta: null,
        files: [],
        dealError: null,
        dealUpdatedNote: false,
      };

    case MESSAGES_REQUEST:
      return { ...state, messagesInProgress: true, messagesError: null };
    case MESSAGES_SUCCESS:
      if (payload.dealId !== state.dealId) return state;
      return {
        ...state,
        messages: payload.replace
          ? mergeMessages([], payload.messages)
          : mergeMessages(state.messages, payload.messages),
        messagesMeta: payload.meta,
        messagesInProgress: false,
      };
    case MESSAGES_ERROR:
      return { ...state, messagesInProgress: false, messagesError: payload };

    case SEND_REQUEST:
      return { ...state, sendInProgress: true, sendError: null };
    case SEND_SUCCESS:
      if (payload.dealId !== state.dealId) return { ...state, sendInProgress: false };
      return {
        ...state,
        sendInProgress: false,
        messages: mergeMessages(state.messages, [payload.message]),
      };
    case SEND_ERROR:
      return { ...state, sendInProgress: false, sendError: payload };

    case ACTION_REQUEST:
      return { ...state, actionInProgress: payload, actionError: null, dealUpdatedNote: false };
    case ACTION_DONE:
      return { ...state, actionInProgress: null };
    case ACTION_ERROR:
      return { ...state, actionInProgress: null, actionError: payload };

    case FILES_REQUEST:
      return { ...state, filesInProgress: true, filesError: null };
    case FILES_SUCCESS:
      if (payload.dealId !== state.dealId) return state;
      return { ...state, filesInProgress: false, files: payload.files };
    case FILES_ERROR:
      return { ...state, filesInProgress: false, filesError: payload };

    case UPDATES_SUCCESS: {
      // Anything loaded after this refresh started (an action, a send, a
      // filter change) is newer than what it brings, so it is kept.
      // The list only when its filters did not change while it was loading.
      const listPart =
        sameQuery(state.query, payload.query) && state.listLoadedAt <= payload.startedAt
        ? {
            rows: payload.rows,
            counts: payload.counts,
            listMeta: payload.meta,
            listLoadedAt: Date.now(),
          }
        : {};
      // The deal only when the same deal is still open.
      const dealPart =
        payload.dealId &&
        payload.dealId === state.dealId &&
        payload.deal &&
        state.dealLoadedAt <= payload.startedAt
          ? {
              deal: payload.deal,
              dealLoadedAt: Date.now(),
              messages: mergeMessages(state.messages, payload.messages || []),
              dealUpdatedNote:
                state.dealUpdatedNote || dealChangedBetween(state.deal, payload.deal),
            }
          : {};
      return { ...state, ...listPart, ...dealPart };
    }
    case DISMISS_UPDATE_NOTE:
      return { ...state, dealUpdatedNote: false };

    default:
      return state;
  }
}

// ================ Thunks ================ //

/**
 * The list query from the URL: ?tab=&stage=&chip=. Pure. `tab` is null when
 * the link names none: the server then lists "Your listings", and the page
 * may switch to another tab (inboxTabFor).
 */
export const queryFromSearch = search => {
  const { tab, stage, chip } = parse(search || '');
  return {
    tab: TABS.includes(tab) ? tab : null,
    stage: stage || 'all',
    chip: chip || null,
  };
};

/**
 * The tab to open when the link names none (PAR-747). Pure.
 *   - A deal is open: its own tab ("Go to chat" on a listing the member
 *     enquired on, or the chat that opens after sending an enquiry).
 *   - "Your listings" is empty but "Your enquiries" is not: "Your enquiries".
 *   - Anything else: "Your listings".
 * Returns null while there is nothing to decide on yet.
 */
export const inboxTabFor = ({ deal, dealId, sizes }) => {
  if (dealId) {
    if (!deal || deal.id !== dealId) return null;
    return deal.side === 'enquirer' ? 'enquiries' : 'listings';
  }
  if (!sizes) return null;
  return !sizes.listings && sizes.enquiries > 0 ? 'enquiries' : 'listings';
};

/** The top bar's dots come from the same counts as the inbox. */
const updateBadge = counts => dispatch => {
  if (!counts?.tabs) return;
  dispatch(
    setUnreadMessagesLog({
      hasUnreadInMyListings: counts.tabs.listings > 0,
      hasUnreadInMyResponses: counts.tabs.enquiries > 0,
      hasUnreadInIntroduced: false,
    })
  );
  // The member area's Inbox badge shows the same number.
  dispatch(setMemberAreaState({ inboxCount: (counts.tabs.listings || 0) + (counts.tabs.enquiries || 0) }));
};

export const fetchInbox = query => dispatch => {
  dispatch({ type: LIST_REQUEST, payload: query });
  return getInbox(query)
    .then(response => {
      dispatch({ type: LIST_SUCCESS, payload: response });
      dispatch(updateBadge(response.counts));
      return response;
    })
    .catch(e => {
      dispatch({ type: LIST_ERROR, payload: errorOf(e) });
    });
};

/** The list again, with the query it was last loaded with. */
export const refreshInbox = () => (dispatch, getState) =>
  dispatch(fetchInbox(getState().DealInboxPage.query));

export const fetchFiles = dealId => dispatch => {
  dispatch({ type: FILES_REQUEST });
  return getTransactionFiles({ transactionId: dealId })
    .then(response =>
      dispatch({ type: FILES_SUCCESS, payload: { dealId, files: response?.batches || [] } })
    )
    .catch(e => dispatch({ type: FILES_ERROR, payload: errorOf(e) }));
};

const dealLoaded = deal => dispatch => {
  dispatch({ type: DEAL_SUCCESS, payload: deal });
  if (deal.allowed?.files) {
    dispatch(fetchFiles(deal.id));
  }
  return deal;
};

/**
 * Open a deal: the deal, its messages, and (after the introduction) its
 * files. Opening it marks it read.
 */
export const fetchDeal = dealId => (dispatch, getState) => {
  dispatch({ type: DEAL_REQUEST, payload: dealId });
  return getInboxDeal(dealId)
    .then(({ deal }) => {
      dispatch(dealLoaded(deal));
      if (deal.unreadCount > 0) {
        readInboxDeal(dealId)
          .then(response => {
            dispatch({ type: DEAL_SUCCESS, payload: response.deal });
            dispatch(refreshInbox());
          })
          .catch(() => {});
      }
      return deal;
    })
    .catch(e => {
      dispatch({ type: DEAL_ERROR, payload: errorOf(e) });
    });
};

export const fetchMessages = (dealId, page = 1) => dispatch => {
  dispatch({ type: MESSAGES_REQUEST });
  return getInboxMessages(dealId, { page })
    .then(({ messages, meta }) =>
      dispatch({
        type: MESSAGES_SUCCESS,
        payload: { dealId, messages, meta, replace: page === 1 },
      })
    )
    .catch(e => dispatch({ type: MESSAGES_ERROR, payload: errorOf(e) }));
};

export const clearDeal = () => ({ type: DEAL_CLEAR });

// One id per message being sent, kept until it goes through: pressing Send
// again after an error reuses it, so the server answers with the message it
// already saved instead of storing it twice.
const pendingSends = new Map();

export const sendMessage = (dealId, content) => dispatch => {
  const key = `${dealId}:${content}`;
  const clientId = pendingSends.get(key) || newClientId();
  pendingSends.set(key, clientId);
  dispatch({ type: SEND_REQUEST });
  return sendInboxMessage(dealId, { content, clientId })
    .then(({ message }) => {
      pendingSends.delete(key);
      dispatch({ type: SEND_SUCCESS, payload: { dealId, message } });
      dispatch(refreshInbox());
      return message;
    })
    .catch(e => {
      // A refused message will be refused again: no need to keep its id.
      if (e?.error) pendingSends.delete(key);
      dispatch({ type: SEND_ERROR, payload: errorOf(e) });
      throw e;
    });
};

/**
 * Run something on the open deal, then show it as it now is.
 *   run: () => Promise<{ deal }>
 *   name: what is running, for the buttons' progress
 */
const runOnDeal = (dealId, name, run, { refreshMessages = false } = {}) => dispatch => {
  dispatch({ type: ACTION_REQUEST, payload: name });
  return run()
    .then(({ deal }) => {
      dispatch({ type: ACTION_DONE });
      dispatch(dealLoaded(deal));
      if (refreshMessages) {
        dispatch(fetchMessages(dealId));
      }
      dispatch(refreshInbox());
      return deal;
    })
    .catch(e => {
      const refusal = errorOf(e);
      dispatch({ type: ACTION_ERROR, payload: refusal });
      // Someone else changed it first: show it as it is now.
      if (['conflict', 'wrong-state'].includes(refusal.error)) {
        dispatch(fetchDeal(dealId));
        dispatch(fetchMessages(dealId));
      }
      throw refusal;
    });
};

/** One of the deal actions (accept-application, request-introduction, ...). */
export const runDealAction = (dealId, action) =>
  runOnDeal(dealId, action, () => inboxDealAction(dealId, action), { refreshMessages: true });

export const setStar = (dealId, starred) =>
  runOnDeal(dealId, 'star', () => starInboxDeal(dealId, starred));

export const markUnread = dealId => runOnDeal(dealId, 'unread', () => unreadInboxDeal(dealId));

/** "Close the Chat" / "Close Chat" on a closed deal: it leaves the inbox. */
export const hideDeal = dealId => runOnDeal(dealId, 'hide', () => hideInboxDeal(dealId));

export const acknowledge = (dealId, kind) =>
  runOnDeal(dealId, `ack-${kind}`, () => ackInboxDeal(dealId, kind));

/**
 * The inbox refresh, every 10 seconds (DealInboxPage starts it while the page is open
 * and the browser tab is visible). One request: the list and counts, the open
 * deal, and only the messages newer than the newest one on screen. Nothing
 * the member is doing is touched: the draft, pop-ups and scrolling live
 * elsewhere.
 *
 *   read: the tab is visible, so the open deal's new items count as read
 *
 * Skipped (resolves null) while something else is loading or running, so
 * the two cannot cross. Rejects on failure, so the caller can wait longer.
 */
export const refreshUpdates = ({ read = false } = {}) => (dispatch, getState) => {
  const state = getState().DealInboxPage;
  if (
    state.listInProgress ||
    state.dealInProgress ||
    state.messagesInProgress ||
    state.sendInProgress ||
    state.actionInProgress
  ) {
    return Promise.resolve(null);
  }
  const { query, dealId, messages, listMeta } = state;
  const newest = messages.length ? messages[messages.length - 1].createdAt : null;
  const known = new Set(messages.map(m => m.id));
  const startedAt = Date.now();

  return getInboxUpdates({
    ...query,
    page: listMeta?.page,
    dealId: dealId || undefined,
    after: dealId && newest ? newest : undefined,
    read: dealId && read ? 1 : undefined,
  }).then(response => {
    dispatch({ type: UPDATES_SUCCESS, payload: { ...response, query, dealId, startedAt } });
    dispatch(updateBadge(response.counts));
    // New files were shared: show them in the Files box.
    const newFiles = (response.messages || []).some(
      m => m.code === 'files-uploaded' && !known.has(m.id)
    );
    if (dealId && newFiles && response.deal?.allowed?.files) {
      dispatch(fetchFiles(dealId));
    }
    return response;
  });
};

export const dismissUpdateNote = () => ({ type: DISMISS_UPDATE_NOTE });

// ================ loadData ================ //

// Opening another deal, or going back to the list, should not reload what is
// already on screen: the list is loaded again when its filters change or it
// is older than this, and a deal when another one opens or it is older.
const FRESH_FOR_MS = 30 * 1000;

export const loadData = (params, search) => (dispatch, getState) => {
  const query = queryFromSearch(search);
  const state = getState().DealInboxPage;
  const now = Date.now();
  const loads = [];

  if (!sameQuery(state.query, query) || now - state.listLoadedAt > FRESH_FOR_MS) {
    loads.push(dispatch(fetchInbox(query)));
  }
  if (params?.id) {
    if (params.id !== state.dealId || !state.deal || now - state.dealLoadedAt > FRESH_FOR_MS) {
      loads.push(dispatch(fetchDeal(params.id)), dispatch(fetchMessages(params.id)));
    }
  } else {
    dispatch(clearDeal());
  }
  return Promise.all(loads);
};
